import React, { useCallback, useState } from 'react';
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Link } from '@mui/material';
import { FormikHelpers } from 'formik';

import Page from '../../../components/Page';
import AuthShell from '../../../components/ui/AuthShell';
import SignupForm, { ISignupValues } from './signupForm'
import { useModal } from '../../../contexts/modal';
import { LoginSteps } from "./model/login-steps";
import { VerifyEmail } from "./form/verify-email";
import { handlePromise } from "../../../utils/handlePromisse";
import { resendOtp, sendOtp, validateOtp } from "../../../services/user-validation/otp.service";
import { SimpleSnackbar } from "../../../components/snackbar";
import { signupWithEmail } from "../../../services/authService";
import MCAlert from "../../../components/ModalContents/MCAlert";

const SignupPage: React.FC = () => {
    const { setModalOpen, setModalContent } = useModal()
    const navigate = useNavigate()
    const [values, setValues] = useState<ISignupValues>({} as ISignupValues)

    const [showSnackbar, setShowSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [step, setStep] = useState<LoginSteps>(LoginSteps.REGISTER)

    const onSubmit = async (values: ISignupValues, actions: FormikHelpers<ISignupValues>) => {
        setStep(LoginSteps.CONFIRMATION)
        setValues(values)
        await sendOtpCode(values.email)
        actions.setSubmitting(false)
    }

    const registerUser = async (values: ISignupValues) => {
        const [, error] = await handlePromise(signupWithEmail(values))
        if (!!error) {
            setModalContent(<MCAlert title="Erro ao realizar cadastro" text={error.response.data.message}/>)
            setModalOpen(true)
        } else {
            setModalContent(
                <MCAlert
                    title="Cadastro bem-sucedido"
                    text="Parabéns 🎉🎉, agradecemos pela preferência"
                    buttonText="ir para login"
                    onClose={() => {
                        navigate('/login', { replace: true })
                        setModalOpen(false)
                    }}
                />
            )
            setModalOpen(true)
        }
    }

    const sendOtpCode = useCallback(async (email: string) => {
        const [_, error] = await handlePromise(sendOtp(email))

        if (!!error) {
            setShowSnackbar(true)
            setSnackbarMessage(error.response.data.message)
            return
        }
    }, [values])

    const validateOtpCode = useCallback(async (otp: string) => {
        const [_, error] = await handlePromise(validateOtp(values.email, otp))

        if (!!error) {
            setShowSnackbar(true)
            setSnackbarMessage(error.response.data.message)
            return
        }

        setShowSnackbar(true)
        setSnackbarMessage('Código validado com sucesso!')
        await registerUser(values)
    }, [values])

    const resendOptCode = useCallback(async () => {
        const [_, error] = await handlePromise(resendOtp(values.email))

        if (!!error) {
            setShowSnackbar(true)
            setSnackbarMessage(error.response.data.message)
            return
        }

        setShowSnackbar(true)
        setSnackbarMessage('Código reenviado com sucesso!')
    }, [values])

    const onBack = () => {
        setStep(LoginSteps.REGISTER)
    }

    const isRegister = step === LoginSteps.REGISTER

    return (
        <Page title="Signup | ProAtivos">
            <SimpleSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                onDismiss={() => setShowSnackbar(false)}
            />

            <AuthShell
                kicker={isRegister ? 'Crie sua conta' : 'Último passo'}
                title={isRegister ? 'Cadastre-se' : 'Confirme seu e-mail'}
                panelBadge={isRegister ? 'Comece grátis' : 'Último passo'}
                panelHeadline={isRegister ? 'Eleve o padrão das suas prescrições.' : 'Falta só confirmar seu e-mail.'}
                panelText={isRegister
                    ? 'Crie sua conta e tenha acesso à biblioteca de ativos, gestão de pacientes e receitas em PDF.'
                    : 'Validamos seu acesso para manter os dados dos seus pacientes sempre seguros.'}
                subtitle={isRegister ? (
                    <>
                        Já tem conta?{' '}
                        <Link component={RouterLink} to="/login" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none' }}>
                            Entrar
                        </Link>
                    </>
                ) : undefined}
            >
                {isRegister ? (
                    <SignupForm typedValues={values} handleSubmitProp={onSubmit} />
                ) : (
                    <VerifyEmail email={values.email} onValidate={validateOtpCode} onResend={resendOptCode} onBack={onBack} />
                )}
            </AuthShell>
        </Page>
    )
}

export default SignupPage
